/**
 * Indian financial year.
 *
 * The FY runs April–March by default (`fyStartMonth: 4`). Dates are
 * 'YYYY-MM-DD' strings throughout, never `Date`, because a `Date` is a UTC
 * instant: `new Date('2026-04-01')` is 31 March in some zones, which would
 * file a 1 April invoice into the previous FY and change its number series.
 */

export interface DateParts {
  year: number;
  /** 1-12 */
  month: number;
  day: number;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function parseIsoDate(value: string): DateParts | null {
  const m = ISO_DATE.exec(String(value ?? '').trim());
  if (!m) return null;
  const [, y, mo, d] = m;
  const year = Number(y);
  const month = Number(mo);
  const day = Number(d);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year, month, day };
}

export function isValidIsoDate(value: string): boolean {
  return parseIsoDate(value) !== null;
}

export function toIsoDate(parts: DateParts): string {
  const mm = String(parts.month).padStart(2, '0');
  const dd = String(parts.day).padStart(2, '0');
  return `${parts.year}-${mm}-${dd}`;
}

/**
 * Financial-year label for a date.
 *
 *   '2026-04-01' with fyStartMonth 4 → '2026-27'
 *   '2026-03-31' with fyStartMonth 4 → '2025-26'
 *   '2026-05-16' with fyStartMonth 7 → '2026-27'  (FY starts in July)
 *
 * The real 1.6.x data has BOTH '25-26' and '26-27' series in one company,
 * so the label is a first-class key, not a display string.
 */
export function financialYearFor(date: string, fyStartMonth = 4): string {
  const parts = parseIsoDate(date);
  if (!parts) {
    throw new RangeError(`Invalid ISO date for financial year: "${date}"`);
  }
  if (fyStartMonth < 1 || fyStartMonth > 12) {
    throw new RangeError(`fyStartMonth must be 1-12, got ${fyStartMonth}`);
  }
  // A date before the FY start month belongs to the previous FY.
  const startYear = parts.month >= fyStartMonth ? parts.year : parts.year - 1;
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`;
}

/** First calendar date of a financial year, e.g. '2026-27' → '2026-04-01'. */
export function financialYearStart(financialYear: string, fyStartMonth = 4): string {
  const startYear = Number(financialYear.slice(0, 4));
  if (!Number.isFinite(startYear)) {
    throw new RangeError(`Invalid financial year: "${financialYear}"`);
  }
  return toIsoDate({ year: startYear, month: fyStartMonth, day: 1 });
}

/** True when `date` falls inside `financialYear`. */
export function isInFinancialYear(
  date: string,
  financialYear: string,
  fyStartMonth = 4,
): boolean {
  return financialYearFor(date, fyStartMonth) === financialYear;
}

/** All financial-year labels overlapping a date range, oldest first. */
export function financialYearsInRange(
  fromDate: string,
  toDate: string,
  fyStartMonth = 4,
): string[] {
  const from = financialYearFor(fromDate, fyStartMonth);
  const to = financialYearFor(toDate, fyStartMonth);
  const startYear = Number(from.slice(0, 4));
  const endYear = Number(to.slice(0, 4));
  const out: string[] = [];
  for (let y = startYear; y <= endYear; y++) {
    out.push(`${y}-${String((y + 1) % 100).padStart(2, '0')}`);
  }
  return out;
}
